import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { twJoin } from 'tailwind-merge';

import { AutoSizedImage } from '@/components/autoSizedImage';
import { EmbeddedVideoPlayer } from '@/components/embeddedVideoPlayer';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { type CancerTypeOption, type ContentLibraryItem } from '@/types/contentLibraryItems';
import { parseInlineMarkup } from '@/utils/inlineMarkup';

interface MatchRange {
  end: number;
  start: number;
}

interface TextSnippet {
  end: number;
  highlight: boolean;
  start: number;
}

// Takes the search and selectedCancerType params to filter the Content Library items and only return those that match all conditions.
export const getFilteredContentLibraryItems = (
  search: string,
  selectedCancerType: number,
  contentLibraryItems: ContentLibraryItem[]
) => {
  const lowercaseSearch = search.trim().toLowerCase();

  return contentLibraryItems.filter((contentLibraryItem) => {
    const filterConditions = [true, true];

    if (lowercaseSearch) {
      const displayedType = contentLibraryItem.typeOverride || contentLibraryItem.type || '';

      filterConditions[0] =
        contentLibraryItem.title.toLowerCase().includes(lowercaseSearch) ||
        contentLibraryItem.shortDescription.toLowerCase().includes(lowercaseSearch) ||
        displayedType.toLowerCase().includes(lowercaseSearch) ||
        contentLibraryItem.cancerTypes.filter((cancerType: CancerTypeOption) =>
          cancerType.name.toLowerCase().includes(lowercaseSearch)
        ).length > 0;
    }

    if (selectedCancerType !== 0 && contentLibraryItem.cancerTypes) {
      const selectedCancerTypeInContentLibraryItemCancerTypes = contentLibraryItem.cancerTypes
        .map((cancerType: CancerTypeOption) => cancerType.id)
        .includes(selectedCancerType);

      const contentLibraryItemHasNoCancerTypes = contentLibraryItem.cancerTypes.length === 0;

      filterConditions[1] =
        selectedCancerTypeInContentLibraryItemCancerTypes || contentLibraryItemHasNoCancerTypes;
    }

    return !filterConditions.includes(false);
  });
};

export const getSortedContentLibraryItems = (
  selectedSortBy: string,
  contentLibraryItems: ContentLibraryItem[]
) => {
  return contentLibraryItems.sort((a, b) => {
    switch (selectedSortBy) {
      case 'newest':
        if (a.createdAt && b.createdAt) {
          return a.createdAt > b.createdAt ? -1 : 1;
        }
      case 'mostLiked':
        return (a?.numLikes || 0) > (b?.numLikes || 0) ? -1 : 1;
    }

    return 0;
  });
};

const getMatchRangesForHighlightedText = (text: string, search: string) => {
  const matchRanges: MatchRange[] = [];

  for (const match of text.matchAll(new RegExp(search, 'gi'))) {
    matchRanges.push({
      end: match.index + match[0].length,
      start: match.index,
    });
  }

  return matchRanges;
};

const getTextSnippetsForHighlightedText = (text: string, matchRanges: MatchRange[]) => {
  const textSnippets: TextSnippet[] = [];

  matchRanges.forEach((matchRange, index) => {
    if (index === 0 && matchRange.start !== 0) {
      textSnippets.push({
        end: matchRange.start,
        highlight: false,
        start: 0,
      });
    }

    textSnippets.push({
      end: matchRange.start,
      highlight: true,
      start: matchRange.end,
    });

    if (matchRanges[index + 1]) {
      if (matchRange.end !== matchRanges[index + 1].start) {
        textSnippets.push({
          end: matchRanges[index + 1].start,
          highlight: false,
          start: matchRange.end,
        });
      }
    } else if (matchRange.end !== text.length) {
      textSnippets.push({
        end: text.length,
        highlight: false,
        start: matchRange.end,
      });
    }
  });

  return textSnippets;
};

export const getHighlightedText = (
  text: string,
  search: string,
  additonalClasses?: string,
  additonalParentClasses?: string
) => {
  if (!search) {
    return text;
  }

  const matchRanges = getMatchRangesForHighlightedText(text, search);

  if (!matchRanges.length) {
    return text;
  }

  const textSnippets = getTextSnippetsForHighlightedText(text, matchRanges);

  return (
    <Text className={additonalParentClasses}>
      {textSnippets.map((textSnippet, index) => {
        if (textSnippet.highlight) {
          return (
            <Text className={twJoin('bg-tertiary-400 text-white', additonalClasses)} key={index}>
              {text.substring(textSnippet.start, textSnippet.end)}
            </Text>
          );
        }

        return (
          <Text className={additonalClasses} key={index}>
            {text.substring(textSnippet.start, textSnippet.end)}
          </Text>
        );
      })}
    </Text>
  );
};

const renderBulletedList = (items: string[], startIndex: number) => {
  if (isWeb) {
    return (
      <ul
        key={`list-${startIndex}`}
        style={{
          listStyleType: 'disc',
          marginTop: 8,
          paddingLeft: 24,
        }}>
        {items.map((item, index) => (
          <li
            key={`${startIndex}-${index}`}
            style={{
              marginTop: index === 0 ? 0 : 8,
            }}>
            <Text>{parseInlineMarkup(item)}</Text>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <View className="mt-2 pl-4" key={`list-${startIndex}`}>
      {items.map((item, i) => (
        <View className="mt-2 flex-row first:mt-0" key={`${startIndex}-${i}`}>
          <Text className="mr-2">•</Text>
          <Text className="flex-1">{parseInlineMarkup(item)}</Text>
        </View>
      ))}
    </View>
  );
};

const parseVideoSource = (
  source: string
): {
  videoId: string;
  videoProvider: 'YouTube' | 'Vimeo';
} | null => {
  const shorthandMatch = source.match(/^(youtube|vimeo):(.+)$/i);

  if (shorthandMatch) {
    const provider = shorthandMatch[1].toLowerCase() === 'youtube' ? 'YouTube' : 'Vimeo';

    return {
      videoId: shorthandMatch[2],
      videoProvider: provider,
    };
  }

  const youtubePatterns = [
    /(?:youtube\.com\/watch\?.*v=)([\w-]+)/,
    /(?:youtu\.be\/)([\w-]+)/,
    /(?:youtube\.com\/embed\/)([\w-]+)/,
  ];

  for (const pattern of youtubePatterns) {
    const match = source.match(pattern);

    if (match) {
      return {
        videoId: match[1],
        videoProvider: 'YouTube',
      };
    }
  }

  const vimeoPatterns = [/(?:vimeo\.com\/)(\d+)/, /(?:player\.vimeo\.com\/video\/)(\d+)/];

  for (const pattern of vimeoPatterns) {
    const match = source.match(pattern);

    if (match) {
      return {
        videoId: match[1],
        videoProvider: 'Vimeo',
      };
    }
  }

  return null;
};

export const getArticleContentMarkup = (
  articleContent: string,
  getCloudFrontUrl: (path: string) => string,
  contentLibraryItemId?: number,
  isPublic?: boolean
) => {
  const contentMarkupLines: any[] = [];

  const articlesLines = articleContent.trim().split(/\n+/);

  let index = 0;

  while (index < articlesLines.length) {
    const articleLine = articlesLines[index];

    if (/^- /.test(articleLine)) {
      // Bulleted list
      const listItems: string[] = [];

      const listStartIndex = index;

      while (index < articlesLines.length && /^- /.test(articlesLines[index])) {
        listItems.push(articlesLines[index].slice(2));

        index += 1;
      }

      contentMarkupLines.push(renderBulletedList(listItems, listStartIndex));
    } else if (/^!\[.*?\]\([^()]*(?:\([^()]*\)[^()]*)*\)$/.test(articleLine)) {
      // Image
      const match = articleLine.match(/!\[(.*?)\]\(([^()]*(?:\([^()]*\)[^()]*)*)\)/);

      if (match && match[1] && match[2]) {
        contentMarkupLines.push(
          <View className="mt-6" key={index}>
            <AutoSizedImage alt={match[1]} imageUrl={getCloudFrontUrl(match[2])} />
          </View>
        );
      }

      index += 1;
    } else if (/^@\[video(?:\s+\d+:\d+)?\]\([^()]*(?:\([^()]*\)[^()]*)*\)$/.test(articleLine)) {
      // Video embed
      const match = articleLine.match(
        /^@\[video(?:\s+(\d+:\d+))?\]\(([^()]*(?:\([^()]*\)[^()]*)*)\)$/
      );

      if (match && match[2]) {
        const videoSource = parseVideoSource(match[2]);

        if (videoSource) {
          let aspectRatio:
            | {
                height: number;
                width: number;
              }
            | undefined;

          if (match[1]) {
            const [w, h] = match[1].split(':').map(Number);

            if (w > 0 && h > 0) {
              aspectRatio = {
                height: h,
                width: w,
              };
            }
          }

          contentMarkupLines.push(
            <View className="mt-6" key={index}>
              <EmbeddedVideoPlayer
                aspectRatio={aspectRatio}
                contentLibraryItemId={contentLibraryItemId}
                isPublic={isPublic}
                videoId={videoSource.videoId}
                videoProvider={videoSource.videoProvider}
              />
            </View>
          );
        }
      }

      index += 1;
    } else if (/^<hr>$/.test(articleLine)) {
      // Divider line
      contentMarkupLines.push(
        <View className="mt-6 border-t border-solid border-background-300" key={index} />
      );

      index += 1;
    } else {
      // Text
      contentMarkupLines.push(
        <Text className="mt-6" key={index}>
          {parseInlineMarkup(articleLine)}
        </Text>
      );

      index += 1;
    }
  }

  return contentMarkupLines;
};
